import { useSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia/components/toast/utils";
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  PrimaryButton,
} from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSWRConfig } from "swr";

import { ActionToast } from "../../../components/action-toast/ActionToast";

export const BULK_SET_CLIENT_DECISION_PATH =
  "/api/internal/client-portal/case/bulk-set-client-decision/";
const CASE_API_PATH = "/api/internal/client-portal/case/";

export interface BulkSetClientDecisionResponse {
  updated: string[];
  skipped: { short_id: string; reason: "NOT_ELIGIBLE" | "FORBIDDEN" }[];
}

interface BulkSetClientDecisionRequest {
  case_short_ids: string[];
  decision: "MEETS_CRITERIA";
}

interface Props {
  selectedCaseIds: string[];
  onActionComplete: () => void;
}

export function BulkMarkMeetsActionBar({
  selectedCaseIds,
  onActionComplete,
}: Props) {
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { submit, isSubmitting } = useSubmit<
    BulkSetClientDecisionResponse,
    BulkSetClientDecisionRequest
  >(BULK_SET_CLIENT_DECISION_PATH, {
    showToastOnError: true,
    onSuccess: (data) => {
      void mutate(
        (key) => typeof key === "string" && key.startsWith(CASE_API_PATH)
      );
      if (data.updated.length > 0) {
        showToast(
          t("Marked {{count}} cases as meets criteria.", {
            count: data.updated.length,
          }),
          { type: "success" }
        );
      }
      if (data.skipped.length > 0) {
        showToast(
          t(
            "{{count}} cases were skipped because they can no longer be marked as meets criteria.",
            { count: data.skipped.length }
          ),
          { type: "info" }
        );
      }
      setIsConfirmOpen(false);
      onActionComplete();
    },
  });

  function handleConfirm() {
    void submit({
      case_short_ids: selectedCaseIds,
      decision: "MEETS_CRITERIA",
    });
  }

  return (
    <>
      <ActionToast
        isOpen={selectedCaseIds.length > 0}
        onDismiss={onActionComplete}
        label={t("{{count}} selected", { count: selectedCaseIds.length })}
        maxWidth="560px"
      >
        <BasicButton
          inverse
          isDisabled={isSubmitting}
          onPress={() => setIsConfirmOpen(true)}
          testID="bulk-mark-meets-button"
        >
          {t("Mark as meets criteria")}
        </BasicButton>
      </ActionToast>
      {isConfirmOpen && (
        <Modal
          isOpen
          onOpenChange={setIsConfirmOpen}
          isKeyboardDismissDisabled={isSubmitting}
          testID="bulk-mark-meets-confirm-modal"
        >
          <Dialog>
            <DialogHeader onClose={() => setIsConfirmOpen(false)}>
              {t("Mark as meets criteria")}
            </DialogHeader>
            <DialogContent>
              {t(
                "Mark {{count}} clear UK right to work cases as meets criteria? They will move out of Review.",
                { count: selectedCaseIds.length }
              )}
            </DialogContent>
            <DialogFooter>
              <BasicButton
                onPress={() => setIsConfirmOpen(false)}
                isDisabled={isSubmitting}
                testID="bulk-mark-meets-cancel-button"
              >
                {t("Cancel")}
              </BasicButton>
              <PrimaryButton
                onPress={handleConfirm}
                isDisabled={isSubmitting}
                testID="bulk-mark-meets-confirm-button"
              >
                {t("Mark as meets criteria")}
              </PrimaryButton>
            </DialogFooter>
          </Dialog>
        </Modal>
      )}
    </>
  );
}
